import { arrayMove } from '@dnd-kit/sortable';

import type {
  TConditionJSON,
  TConditionNode,
  TConditionsOutput,
  TGroupJSON,
  TGroupNode,
  TLogicalOperator,
  TNode,
  TRuleBuilderKind,
} from './types';

const SEGMENT_OPERATOR = 'segment';
const VARIANT_SCHEMA_OPERATOR = 'variantSchema';
const DATE_MATH = 'dateMath';
const PATH_PREFIX = '$.';
const ALLOWED_PATH_TYPES = new Set(['string', 'number', 'boolean', 'date', 'array']);

function stripPathPrefix(value: string): string {
  const trimmed = value.trim();
  return trimmed.startsWith(PATH_PREFIX) ? trimmed.slice(PATH_PREFIX.length) : trimmed;
}

/** Normalizes any path input into its persisted form using exactly one `$.` prefix. */
export function normalizePathToStored(path: string): string {
  const basePath = stripPathPrefix(path);
  if (!basePath) return '';
  return `${PATH_PREFIX}${basePath}`;
}

/** Normalizes any stored path to its UI form (without `$.` prefix). */
export function normalizePathForUI(path: string): string {
  return stripPathPrefix(path);
}

function isValidPathType(pathType: unknown): pathType is string {
  return typeof pathType === 'string' && ALLOWED_PATH_TYPES.has(pathType.toLowerCase().trim());
}

/** Generates a collision-resistant unique ID for tree nodes. */
export function generateId(): string {
  return crypto.randomUUID();
}

/** Returns true when a condition value should be considered empty / not yet filled. */
function isConditionValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value) && value !== null && 'ranges' in value) {
    const v = value as Record<string, unknown>;
    return (
      typeof v['saltBase'] !== 'string' ||
      (v['saltBase'] as string).trim() === '' ||
      !Array.isArray(v['ranges']) ||
      (v['ranges'] as unknown[]).length === 0
    );
  }
  return false;
}

/** Returns true when a `dateMath(...)` string is missing a non-empty `baseDate` argument. */
export function isDateMathMissingBaseDate(value: unknown): boolean {
  if (typeof value !== 'string' || !value.includes(DATE_MATH)) return false;

  const start = value.indexOf('{');
  const end = value.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) return true;

  const inner = value.slice(start + 1, end);
  const params = inner
    .split(',')
    .map((param) => param.trim())
    .filter(Boolean);

  let hasBaseDate = false;

  for (const param of params) {
    const [rawKey, ...rest] = param.split(':');
    if (!rawKey || rest.length === 0) continue;

    if (rawKey.trim() !== 'baseDate') continue;

    hasBaseDate = true;
    const rawValue = rest.join(':').trim();

    const parsedValue =
      (rawValue.startsWith('"') && rawValue.endsWith('"')) ||
      (rawValue.startsWith("'") && rawValue.endsWith("'"))
        ? rawValue.slice(1, -1).trim()
        : rawValue.trim();

    if (parsedValue !== '') return false;
  }

  return !hasBaseDate;
}

/** Creates a new empty condition node with no fact, operator, or value. */
export function createEmptyCondition(isBaseCondition?: boolean): TConditionNode {
  return {
    id: generateId(),
    type: 'condition',
    fact: '',
    operator: '',
    value: '',
    ...(isBaseCondition ? { isBaseCondition: true } : {}),
  };
}

/** Creates a new group node with operator `all` and one empty child condition. */
export function createEmptyGroup(isBaseCondition?: boolean): TGroupNode {
  return {
    id: generateId(),
    type: 'group',
    operator: 'all',
    children: [createEmptyCondition(isBaseCondition)],
  };
}

/**
 * Creates the default root group used when no initial `value` is provided.
 * Equivalent to `createEmptyGroup` but semantically marks the root intent.
 */
export function createDefaultRoot(): TGroupNode {
  return {
    id: generateId(),
    type: 'group',
    operator: 'all',
    children: [],
  };
}

/** Returns true when a condition node uses the `variantSchema` operator. */
export function isVariantSchemaCondition(node: TNode): node is TConditionNode {
  return node.type === 'condition' && node.operator === VARIANT_SCHEMA_OPERATOR;
}

/** Returns true when the node is the locked base `segment` condition for plain rule flows. */
export function isLockedBaseSegmentCondition(
  node: TNode | null | undefined,
  kind: TRuleBuilderKind,
): boolean {
  return (
    kind === 'rule' &&
    node?.type === 'condition' &&
    node.isBaseCondition === true &&
    node.operator === SEGMENT_OPERATOR
  );
}

/** Returns whether the node should be rendered in the builder UI. */
export function isNodeVisibleInBuilder(node: TNode, kind: TRuleBuilderKind): boolean {
  if (node.type === 'condition') {
    return !(
      kind === 'rule' &&
      node.isBaseCondition === true &&
      node.operator !== SEGMENT_OPERATOR
    );
  }

  // Keep empty groups visible so users can preserve structure and only delete
  // a group explicitly with the "Eliminar grupo" action.
  if (node.children.length === 0) return true;

  return node.children.some((child) => isNodeVisibleInBuilder(child, kind));
}

/**
 * Returns whether `variantSchema` can be selected for a condition in the current tree.
 *
 * Rules:
 * - Available only when `kind === 'rule-with-variants'`.
 * - Available only for conditions whose parent group is the root group.
 * - Only one root condition can use `variantSchema` at a time.
 */
export function canUseVariantSchemaOperator(
  root: TGroupNode,
  nodeId: string,
  kind: TRuleBuilderKind,
): boolean {
  if (kind !== 'rule-with-variants') return false;

  const parentGroupId = findParentGroupId(root, nodeId);
  if (parentGroupId !== root.id) return false;

  const rootVariantSchemaNode = root.children.find(
    (child) => isVariantSchemaCondition(child) && child.id !== nodeId,
  );
  return !rootVariantSchemaNode;
}

/**
 * Ensures existing trees comply with `variantSchema` business rules.
 *
 * Invalid `variantSchema` conditions are reset to an empty operator/value pair.
 */
export function normalizeVariantSchemaUsage(root: TGroupNode, kind: TRuleBuilderKind): TGroupNode {
  let rootVariantSchemaOwnerId: string | null = null;

  const walk = (group: TGroupNode): TGroupNode => ({
    ...group,
    children: group.children.map((child) => {
      if (child.type === 'group') return walk(child);

      let normalizedChild: TConditionNode = child;

      if (normalizedChild.operator === VARIANT_SCHEMA_OPERATOR) {
        const isRootCondition = group.id === root.id;
        const canKeep =
          kind === 'rule-with-variants' &&
          isRootCondition &&
          (rootVariantSchemaOwnerId === null || rootVariantSchemaOwnerId === normalizedChild.id);

        if (canKeep) {
          rootVariantSchemaOwnerId = normalizedChild.id;
        } else {
          normalizedChild = { ...normalizedChild, operator: '', value: '' };
        }
      }

      if (kind === 'rule-with-variants') {
        return { ...normalizedChild, isBaseCondition: true };
      }

      return normalizedChild;
    }),
  });

  return walk(root);
}

// ─── JSON ↔ Tree conversions ─────────────────────────────────────────────────

/**
 * Converts a `json-rules-engine` compatible `TConditionsOutput` JSON object
 * into the internal mutable tree representation.
 */
export function jsonToTree(json: TConditionsOutput): TGroupNode {
  return jsonGroupToNode(json);
}

function jsonGroupToNode(json: TGroupJSON): TGroupNode {
  if ('all' in json) {
    return {
      id: generateId(),
      type: 'group',
      operator: 'all',
      children: (json as { all: (TConditionJSON | TGroupJSON)[] }).all.map(jsonChildToNode),
    };
  }
  if ('any' in json) {
    return {
      id: generateId(),
      type: 'group',
      operator: 'any',
      children: (json as { any: (TConditionJSON | TGroupJSON)[] }).any.map(jsonChildToNode),
    };
  }
  return {
    id: generateId(),
    type: 'group',
    operator: 'not',
    children: [jsonChildToNode((json as { not: TConditionJSON | TGroupJSON }).not)],
  };
}

function jsonChildToNode(json: TConditionJSON | TGroupJSON): TNode {
  if ('fact' in json) {
    const conditionJson = json as TConditionJSON;
    return {
      id: generateId(),
      type: 'condition',
      fact: conditionJson.fact,
      ...(conditionJson.factType !== undefined ? { factType: conditionJson.factType } : {}),
      ...(conditionJson.path !== undefined ? { path: conditionJson.path } : {}),
      ...(conditionJson.pathType !== undefined ? { pathType: conditionJson.pathType } : {}),
      operator: conditionJson.operator,
      value: conditionJson.value ?? '',
      ...(conditionJson.isBaseCondition !== undefined
        ? { isBaseCondition: conditionJson.isBaseCondition }
        : {}),
    };
  }
  if ('all' in json || 'any' in json || 'not' in json) {
    return jsonGroupToNode(json as TGroupJSON);
  }
  // Malformed entry: not a condition nor a group → fall back to an empty condition
  return createEmptyCondition();
}

/**
 * Serializes the internal tree representation back into a
 * `json-rules-engine` compatible `TConditionsOutput` JSON object.
 */
export function treeToJSON(node: TGroupNode): TConditionsOutput {
  return groupToJSON(node);
}

function groupToJSON(node: TGroupNode): TGroupJSON {
  const serializedChildren = node.children.map(nodeToJSON);
  if (node.operator === 'all') return { all: serializedChildren };
  if (node.operator === 'any') return { any: serializedChildren };
  return { not: serializedChildren[0] ?? { fact: '', operator: '', value: '' } };
}

function nodeToJSON(node: TNode): TConditionJSON | TGroupJSON {
  if (node.type === 'condition') {
    return {
      fact: node.fact,
      ...(node.factType !== undefined ? { factType: node.factType } : {}),
      ...(node.path !== undefined ? { path: node.path } : {}),
      ...(node.pathType !== undefined ? { pathType: node.pathType } : {}),
      operator: node.operator,
      value: node.value,
      ...(node.isBaseCondition !== undefined ? { isBaseCondition: node.isBaseCondition } : {}),
    };
  }
  return groupToJSON(node);
}

// ─── Tree mutations ───────────────────────────────────────────────────────────

/** Returns a new tree with an empty condition appended to the specified group. */
export function addConditionToGroup(
  root: TGroupNode,
  groupId: string,
  isBaseCondition?: boolean,
): TGroupNode {
  return mapGroup(root, (group) => {
    if (group.id !== groupId) return group;
    return { ...group, children: [...group.children, createEmptyCondition(isBaseCondition)] };
  });
}

/** Returns a new tree with a new nested group appended to the specified group. */
export function addGroupToGroup(
  root: TGroupNode,
  groupId: string,
  isBaseCondition?: boolean,
): TGroupNode {
  return mapGroup(root, (group) => {
    if (group.id !== groupId) return group;
    return { ...group, children: [...group.children, createEmptyGroup(isBaseCondition)] };
  });
}

/** Returns a new tree with the node matching `nodeId` removed from the tree. */
export function deleteNodeById(root: TGroupNode, nodeId: string): TGroupNode {
  return mapGroup(root, (group) => ({
    ...group,
    children: group.children.filter((child) => child.id !== nodeId),
  }));
}

/** Returns a new tree with the condition node matching `nodeId` partially updated. */
export function updateConditionNode(
  root: TGroupNode,
  nodeId: string,
  updates: Partial<TConditionNode>,
): TGroupNode {
  return mapNode(root, (node) => {
    if (node.id === nodeId && node.type === 'condition') return { ...node, ...updates };
    return node;
  });
}

/** Returns a new tree with the group's logical operator updated. */
export function updateGroupOperator(
  root: TGroupNode,
  groupId: string,
  operator: TLogicalOperator,
): TGroupNode {
  return mapGroup(root, (group) => {
    if (group.id !== groupId) return group;
    return { ...group, operator };
  });
}

/**
 * Returns `true` if `candidateId` is found anywhere inside the subtree
 * rooted at the node with `ancestorId`.
 */
export function isDescendantOf(root: TGroupNode, ancestorId: string, candidateId: string): boolean {
  const ancestorNode = findNodeById(root, ancestorId);
  if (!ancestorNode || ancestorNode.type !== 'group') return false;
  return findNodeById(ancestorNode, candidateId) !== null;
}

/**
 * Returns a new tree with the node `nodeId` moved into `targetGroupId` at `targetIndex`.
 * Performs safety checks to prevent moving a node into itself or its own subtree.
 */
export function moveNodeInTree(
  root: TGroupNode,
  nodeId: string,
  targetGroupId: string,
  targetIndex: number,
): TGroupNode {
  if (nodeId === targetGroupId) return root;
  if (isDescendantOf(root, nodeId, targetGroupId)) return root;
  const nodeToMove = findNodeById(root, nodeId);
  if (!nodeToMove) return root;
  let updatedRoot = removeNodeById(root, nodeId);
  updatedRoot = insertNodeIntoGroup(updatedRoot, targetGroupId, nodeToMove, targetIndex);
  return updatedRoot;
}

/**
 * Returns a new tree with the children of `groupId` reordered by swapping
 * the items at `oldIndex` and `newIndex` using `arrayMove`.
 */
export function reorderWithinGroup(
  root: TGroupNode,
  groupId: string,
  oldIndex: number,
  newIndex: number,
): TGroupNode {
  return mapGroup(root, (group) => {
    if (group.id !== groupId) return group;
    return { ...group, children: arrayMove(group.children, oldIndex, newIndex) };
  });
}

/**
 * Searches the tree depth-first and returns the node with the matching `id`,
 * or `null` if not found.
 */
export function findNodeById(root: TGroupNode, id: string): TNode | null {
  if (root.id === id) return root;
  for (const child of root.children) {
    if (child.id === id) return child;
    if (child.type === 'group') {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Searches the tree depth-first and returns the ID of the direct parent group
 * of the node with `nodeId`, or `null` if the node is not found.
 */
export function findParentGroupId(root: TGroupNode, nodeId: string): string | null {
  for (const child of root.children) {
    if (child.id === nodeId) return root.id;
    if (child.type === 'group') {
      const found = findParentGroupId(child, nodeId);
      if (found) return found;
    }
  }
  return null;
}

function removeNodeById(root: TGroupNode, nodeId: string): TGroupNode {
  return mapGroup(root, (group) => ({
    ...group,
    children: group.children.filter((child) => child.id !== nodeId),
  }));
}

function insertNodeIntoGroup(
  root: TGroupNode,
  groupId: string,
  node: TNode,
  index: number,
): TGroupNode {
  return mapGroup(root, (group) => {
    if (group.id !== groupId) return group;
    const updatedChildren = [...group.children];
    const safeIndex = Math.max(0, Math.min(index, updatedChildren.length));
    updatedChildren.splice(safeIndex, 0, node);
    return { ...group, children: updatedChildren };
  });
}

/**
 * Recursively applies `fn` to every group node in the tree (post-order).
 * Returns a structurally new tree; non-group (condition) children are passed through unchanged.
 */
function mapGroup(node: TGroupNode, fn: (group: TGroupNode) => TGroupNode): TGroupNode {
  const mappedNode = fn(node);
  return {
    ...mappedNode,
    children: mappedNode.children.map((child) =>
      child.type === 'group' ? mapGroup(child, fn) : child,
    ),
  };
}

/**
 * Recursively applies `fn` to every node in the tree (conditions and groups alike).
 * Returns a structurally new tree.
 */
function mapNode(node: TGroupNode, fn: (n: TNode) => TNode): TGroupNode {
  const mappedNode = fn(node) as TGroupNode;
  return {
    ...mappedNode,
    children: mappedNode.children.map((child) =>
      child.type === 'group' ? mapNode(child, fn) : (fn(child) as TConditionNode),
    ),
  };
}

// ─── Validation ───────────────────────────────────────────────────────────────

/**
 * Validates the entire rule tree and returns the set of invalid node IDs.
 *
 * A node is considered invalid if:
 * - It is a group with zero children.
 * - It is a condition missing a `fact`, `operator`, or `value`.
 *
 * @returns `{ isRuleValid, invalidIds }` — whether the tree is valid and which IDs failed.
 */
export function validateTree(
  root: TGroupNode,
  kind: TRuleBuilderKind = 'rule',
): { isRuleValid: boolean; invalidIds: Set<string> } {
  const invalidIds = new Set<string>();

  function checkGroup(group: TGroupNode) {
    const visibleChildren = group.children.filter((child) => isNodeVisibleInBuilder(child, kind));

    if (visibleChildren.length === 0) {
      invalidIds.add(group.id);
    }
    for (const child of visibleChildren) {
      if (child.type === 'condition') {
        const isObjectFactType = child.factType?.toLowerCase().trim() === 'object';
        const normalizedPath = normalizePathToStored(child.path ?? '');
        const hasInvalidObjectPathFields =
          isObjectFactType && (!normalizedPath || !isValidPathType(child.pathType));

        if (
          !child.fact ||
          !child.operator ||
          hasInvalidObjectPathFields ||
          isConditionValueEmpty(child.value) ||
          isDateMathMissingBaseDate(child.value)
        ) {
          invalidIds.add(child.id);
        }
      } else {
        checkGroup(child);
      }
    }
  }

  checkGroup(root);
  return { isRuleValid: invalidIds.size === 0, invalidIds };
}

// ─── Business rules ───────────────────────────────────────────────────────────

/**
 * Returns the list of logical operators that can be selected for a group,
 * based on its current children and its parent's operator.
 *
 * Rules:
 * - `not` is only allowed when the group has ≤ 1 child, the parent is not `not`,
 *   and the first child is not itself a `not` group (no consecutive `not`s).
 */
export function getAllowedOperators(
  group: TGroupNode,
  parentOperator: TLogicalOperator | null,
): TLogicalOperator[] {
  const hasMultipleChildren = group.children.length > 1;
  const parentIsNot = parentOperator === 'not';

  if (hasMultipleChildren || parentIsNot) return ['all', 'any'];

  const firstChild = group.children[0];
  if (firstChild?.type === 'group' && firstChild.operator === 'not') {
    return ['all', 'any'];
  }

  return ['all', 'any', 'not'];
}

/**
 * Returns `true` if the group can accept additional child nodes.
 * `not` groups are limited to exactly one child.
 */
export function canAddToGroup(group: TGroupNode): boolean {
  if (group.operator === 'not') return group.children.length === 0;
  return true;
}
